const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://contract.tirebank.kr";

/**
 * 네이버 지도용 매장 데이터 조회 서비스
 */
export class CompanyMapService {
  /**
   * 네이버 지도용 매장 좌표 목록 조회
   * @param {Object} params - 검색 파라미터
   * @param {boolean} params.active - 활성 매장만 조회 (기본값: true)
   * @param {string} params.search - 검색 키워드
   * @param {number} params.page - 페이지 번호 (0부터 시작)
   * @param {number} params.size - 페이지 크기
   * @param {string} params.sort - 정렬 기준 (기본값: id)
   * @param {string} params.direction - 정렬 방향 (ASC/DESC, 기본값: DESC)
   * @returns {Promise<Object>} 매장 데이터 응답
   */
  static async getCompaniesForNaverMap(params = {}) {
    try {
      const {
        active = true,
        search,
        page,
        size,
        sort = "id",
        direction = "DESC",
      } = params;

      // URL 파라미터 구성
      const urlParams = new URLSearchParams();
      urlParams.append("active", active.toString());

      if (search) {
        urlParams.append("search", search);
      }

      if (page !== undefined) {
        urlParams.append("page", page.toString());
      }

      if (size !== undefined) {
        urlParams.append("size", size.toString());
      }

      urlParams.append("sort", sort);
      urlParams.append("direction", direction);

      const apiUrl = `${API_BASE_URL}/api/companies/naver-map-locations?${urlParams.toString()}`;

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          // 인증이 필요한 경우 토큰 추가
          // 'Authorization': `Bearer ${token}`
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("API 에러 응답:", errorText);
        throw new Error(
          `HTTP error! status: ${response.status}, message: ${errorText}`
        );
      }

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const textResponse = await response.text();
        console.error(
          "JSON이 아닌 응답 받음:",
          textResponse.substring(0, 200) + "..."
        );
        throw new Error(
          "서버에서 JSON이 아닌 응답을 받았습니다. 백엔드 서버가 실행 중인지 확인해주세요."
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("매장 데이터 조회 중 오류 발생:", error);
      throw error;
    }
  }

  /**
   * 모든 매장 데이터 조회 (페이징 없이)
   * @param {boolean} active - 활성 매장만 조회
   * @returns {Promise<Array>} 매장 데이터 배열
   */
  static async getAllCompaniesForMap(active = true) {
    try {
      const apiUrl = `${API_BASE_URL}/api/companies/naver-map-locations?active=${active}`;

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("API 에러 응답:", errorText);
        throw new Error(
          `HTTP error! status: ${response.status}, message: ${errorText}`
        );
      }

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const textResponse = await response.text();
        console.error(
          "JSON이 아닌 응답 받음:",
          textResponse.substring(0, 200) + "..."
        );
        throw new Error(
          "서버에서 JSON이 아닌 응답을 받았습니다. 백엔드 서버가 실행 중인지 확인해주세요."
        );
      }

      const data = await response.json();

      // 페이징된 응답인지 확인 (content 속성이 있으면 페이징 응답)
      if (data.content && Array.isArray(data.content)) {
        return data.content;
      }

      // 배열 응답이면 그대로 반환
      if (Array.isArray(data)) {
        return data;
      }

      console.warn("예상하지 못한 응답 형태:", data);
      return [];
    } catch (error) {
      console.error("전체 매장 데이터 조회 중 오류 발생:", error);
      throw error;
    }
  }

  /**
   * 테스트용 더미 데이터 반환 (백엔드 서버가 없을 때 사용)
   * @returns {Array} 테스트용 매장 데이터
   */
  static getDummyCompaniesForTesting() {
    return [
      {
        id: 11,
        storeCode: "S01242",
        storeNumber: "002",
        storeName: "한누리점",
        trustee: "타이어 뱅크(한누리점)",
        trusteeCode: "202507210011",
        businessNumber: "108-48-94189",
        companyName: "타이어 뱅크(한누리점)",
        representativeName: "한누리",
        active: true,
        startDate: "2025-07-21",
        endDate: "2025-07-26",
        managerName: "한누리",
        email: "bae4445@gmail.com",
        phoneNumber: "010-9357-4454",
        storeTelNumber: "043-856-7340",
        address: "세종특별자치시 한누리대로 350 뱅크빌딩 6층",
        latitude: 36.495415,
        longitude: 127.2572294,
        businessType: "도소매,서비스",
        businessCategory: "상품대리,기타도급",
        branchGroupName: "대전본부",
      },
      {
        id: 10,
        storeCode: "S01112",
        storeNumber: "167",
        storeName: "춘천역점",
        trustee: "이현지타이어(춘천역점)",
        trusteeCode: "202506040010",
        businessNumber: "493-32-01575",
        companyName: "이현지타이어(춘천역점)",
        representativeName: "이현지",
        active: true,
        startDate: "2025-03-05",
        endDate: "2026-03-04",
        managerName: "이현지",
        email: "example@example.com",
        phoneNumber: "010-7259-9698",
        storeTelNumber: "010-7259-9698",
        address: "강원도 춘천시 우두 1길 129 206동 1404호(파크에뷰)",
        latitude: 37.9118355,
        longitude: 127.7353747,
        businessType: "상품대리,기타도급",
        businessCategory: "도소매,서비스",
        branchGroupName: "강원본부",
      },
      {
        id: 9,
        storeCode: "S00865",
        storeNumber: "431",
        storeName: "장호원점",
        trustee: "박규상타이어(장호원점)",
        trusteeCode: "202506040009",
        businessNumber: "773-20-02313",
        companyName: "박규상타이어(장호원점)",
        representativeName: "박규상",
        active: true,
        startDate: "2025-03-07",
        endDate: "2027-03-06",
        managerName: "박규상",
        email: "example@example.com",
        phoneNumber: "010-5405-1650",
        storeTelNumber: "010-5405-1650",
        address:
          "충청남도 천안시 동남구 봉서8길 13, 301동 1405호 (봉명동,봉명청솔아파트)",
        latitude: 36.8108627,
        longitude: 127.1326887,
        businessType: "상품대리,기타도급",
        businessCategory: "도소매,서비스",
        branchGroupName: "강원본부",
      },
      {
        id: 8,
        storeCode: "S01268",
        storeNumber: "610",
        storeName: "하남감일점",
        trustee: "육지육타이어(하남감일점)",
        trusteeCode: "202506040008",
        businessNumber: "601-12-91739",
        companyName: "육지육타이어(하남감일점)",
        representativeName: "육지육",
        active: true,
        startDate: "2025-03-07",
        endDate: "2027-03-06",
        managerName: "육지육",
        email: "example@example.com",
        phoneNumber: "010-8739-3436",
        storeTelNumber: "010-8739-3436",
        address: "경기도 남양주시 경춘로1286번안길 23-9, 302호 (평내동)",
        latitude: 37.6467684,
        longitude: 127.2365144,
        businessType: "상품대리,기타도급",
        businessCategory: "도소매,서비스",
        branchGroupName: "강원본부",
      },
      {
        id: 7,
        storeCode: "S01244",
        storeNumber: "592",
        storeName: "동원주점",
        trustee: "김경은타이어(동원주점)",
        trusteeCode: "202506040007",
        businessNumber: "436-12-02796",
        companyName: "김경은타이어(동원주점)",
        representativeName: "김경은",
        active: true,
        startDate: "2025-03-14",
        endDate: "2027-03-13",
        managerName: "김경은",
        email: "example@example.com",
        phoneNumber: "010-3622-2708",
        storeTelNumber: "010-3622-2708",
        address:
          "강원특별자치도 원주시 시청로 68, 107동 1007호 (무실동,원주무실1단지아파트)",
        latitude: 37.3360481,
        longitude: 127.9230671,
        businessType: "상품대리,기타도급",
        businessCategory: "도소매,서비스",
        branchGroupName: "강원본부",
      },
    ];
  }
}

export default CompanyMapService;
